import {_} from 'lodash';
import {Cookie} from 'js-cookie';
import {CookieAttributes} from "js-cookie";
import * as ko from "knockout";
import {ILogin, IOrganization, IRecievedMessage, TUserRolePersona} from "../index";
import {
    CHECK_FOR_UPDATES_THROTTLE_DURATION,
    Permission,
    SESSION_DURATION,
    SNACKBAR_DURATION_LONG,
    SNACKBAR_DURATION_MEDIUM,
    SPINNER,
    TOKEN_KEY,
    TOKEN_KEY_SUPER,
    USER_HASH_KEY
} from "../constants";
import {config, global, importFormatManager, lm} from "../appContext";
import {KOSubscriptionTracker} from "../util/KOSubscriptionTracker";
import {
    _d,
    createErrorMessage,
    generateHash,
    getEadmRole,
    getPermissionDefinition,
    getRawSyncFieldValue,
    getSyncFieldValue
} from "../util/misc";
import PromiseRequest from "../req/PromiseRequest";
import {nsFa} from "../component/fa/fn";
import pubsub from "../pubsub";
import {initStore} from "../util/sessionstore";
import {getHash, navigateForOrgChange, setRouteHash} from "../constants/RouteDefinitions";
import {hasher} from "hasher";
import {convertFromServerString} from "../util/datetime";
import {IDialogOptionsComponent} from "../component/dialog/index";
import {nsClone} from "../clone/fn";
import {hubSpotSupport} from "../tracking/HubSpotSupport";
import {nsLogin} from "../util/nsLogin";
import {moment} from 'moment';
import {nsAmplify} from "../util/nsAmplify";
import {captureIntendedHash, getOrgnrFromHash, navigateAfterLoginSuccess, sanitizeIntendedHash, stripRouteHash} from "./navigateAfterLogin";
import i = nsFa.i;
import {TSyncfield_User} from "../constants/Syncfields";
import {AuthenticationTypeFlag} from "../constants/AuthenticationTypeFlag";

export class Login {
    selectedOrganizationId: KnockoutComputed<string | null>;

    isLoggingIn: KnockoutObservable<boolean>;
    isLoggingOut: KnockoutObservable<boolean>;
    /** Etter OK innlogging: eHub + importFormat lastes i bakgrunnen mens app-skallet allerede vises. */
    isFinishingLoginBootstrap: KnockoutObservable<boolean> = ko.observable(false);
    isLoggedIn: KnockoutComputed<boolean>;

    organizations: KnockoutObservableArray<IOrganization> = ko.observableArray<IOrganization>([]);
    selectedOrganization: KnockoutObservable<IOrganization | null> = ko.observable(null);

    // whether we're in the process of logging in or out
    isProcessing: KnockoutComputed<boolean>;

    user: KnockoutObservable<ILogin | null> = ko.observable(null);

    private koSub: KOSubscriptionTracker = new KOSubscriptionTracker();

    private readonly reqCheckForUpdates = global.getPromiseRequest('checkforupdates');
    private readonly reqLogOut: PromiseRequest = new PromiseRequest("logout");
    private readonly reqLogin: PromiseRequest = new PromiseRequest('login');

    private redirectUrl: string | undefined;
    public intendedHash: string | null = null;

    private checkForMessagesTimeoutId: ReturnType<typeof setTimeout> | undefined;
    private loginStart: Date | undefined;
    private loginEnd!: Date | null;
    public silentOrgChange: boolean = false;
    public isNewUser!: boolean;

    constructor(private tokenKey: string = TOKEN_KEY) {

        // Capture the deep link the user opened before any router rewrite runs (Login is
        // constructed before Router.init) so login can restore it. Auth-chrome/empty → null.
        this.intendedHash = sanitizeIntendedHash(document.location.hash);

        // If we detect that the user is not authenticated (anymore) we'll remove token and user data and ask the user
        // to login again.
        pubsub.on(this, "not_authenticated", () => {
            this.doSessionDurationEnd()
            global.messageData(null);
            this.removeToken();
            this.user(null);
            this.organizations([]);
            this.selectedOrganization(null);
            this.reqLogin.abort();
            setRouteHash('formlogin')
            global.dialogData({
                title: `${i('fa-exclamation-triangle')} ((not authenticated))`,
                body: `<p>((your session has expired, and you are no longer authenticated.))</p><p>((please log in again to continue.))</p>`,
                confirmLabel: `Ok`,
                onConfirm: () => this.showLogoutMessage(),
                onClose: () => this.showLogoutMessage(),
            });
        });

        pubsub.on(this, 'messages.tasks.updated', this.relogin.bind(this));

        this.koSub.subscribe(this.selectedOrganization, (org: IOrganization | null) => {
            if (org) {
                nsAmplify.store("storedOrganizationId", org.organizationid);
            }

            this.stopCheckForMessages();
            if (org?.ehub) {
                this.startCheckForMessages();
            }
        });

        //--------------------------------------------------------------------------------------------------------------
        // computed properties
        //--------------------------------------------------------------------------------------------------------------

        this.isLoggingIn = this.reqLogin.isRequesting;
        this.isLoggingOut = ko.observable(false);
        this.isLoggedIn = ko.pureComputed((): boolean => !!this.user());
        this.isProcessing = ko.pureComputed((): boolean => this.isLoggingIn() || this.isLoggingOut());
        this.selectedOrganizationId = ko.pureComputed((): string | null => {
            const org = this.selectedOrganization();
            return org?.organizationid ?? null;
        });

    }

    /**
     * Resolve session from hash/cookie before the app shell paints.
     * Awaits automatic login/logout; interactive paths (e.g. multi-token) resolve immediately.
     */
    doStartup(): Promise<void> {
        // Deep link was already captured in the constructor; don't clobber it with the
        // (possibly rewritten) current hash. Token/redirect parsing still uses the live boot hash.
        const bootHash = document.location.hash
        captureIntendedHash(bootHash)
        _d.log(`Boot hash: "${bootHash}"`)
        const segments = bootHash.split('/')
        switch (segments[1]) {
            case 'login': {
                if (segments[3]) {
                    this.redirectUrl = nsLogin.decodeRedirectUrl(segments[3])
                    _d.log(`Storing redirect url: "${this.redirectUrl}"`)
                }
                const urlTokens = segments[2]?.split('|') ?? []
                window.location.hash = getHash('login') ?? '' // don't show the tokens longer than necessary
                switch (urlTokens.length) {
                    case 0: {
                        _d.log('Do login abort (missing token)')
                        setRouteHash('formlogin')
                        return Promise.resolve()
                    }
                    case 1: {
                        const urlToken = urlTokens[0]
                        if (urlToken) {
                            if (urlToken == this.getToken()) {
                                _d.log('Do login (cookie)')
                            } else {
                                _d.log('Do login (url token)')
                                this.setToken(urlToken)
                            }
                            return this.doLogin().catch(() => undefined)
                        }
                        _d.log('No login (missing token)')
                        setRouteHash('formlogin')
                        return Promise.resolve()
                    }
                    default: {
                        _d.log('Do login (url multi token)')
                        this.removeToken()
                        const dialogOptions: IDialogOptionsComponent = {
                            content: {
                                name: 'component/login/dialog/multi-token',
                                params: {tokens: urlTokens},
                            },
                            isClosable: ko.observable(false),
                            title: `${i('fa-sign-in')} ((choose subject|s:((user profile|ac))))`,
                            onSuccess: (params: [ILogin, string]) => {
                                this.setToken(params[1])
                                this.onLoginSuccess(params[0])
                                void this.finishLoginBootstrap()
                            },
                            onError: () => {
                                setRouteHash('formlogin')
                                global.snackbarData(createErrorMessage(`error: could not log in`));
                            }
                        }
                        global.dialogManager.addComponent(dialogOptions)
                        return Promise.resolve()
                    }
                }
            }
            case 'logout': {
                _d.log('Do logout')
                return this.doLogout().catch(() => undefined)
            }
            default: {
                if (this.getToken()) {
                    _d.log('Do login (cookie)')
                    return this.doLogin().catch(() => undefined)
                }
                _d.log(`Re-asserting boot hash: "${bootHash}"`)
                // replaceHash (not parseHash alone) so location.hash matches the activated route.
                hasher.replaceHash(stripRouteHash(bootHash))
                return Promise.resolve()
            }
        }
    }

    /*******************************************************************************************************************
     * Public methods
     ******************************************************************************************************************/

    doLogin(options?: {
        skipLoadingBanner?: boolean;
        /** Runs after profile bootstrap, immediately before post-login routing (e.g. login-card exit fade). */
        beforeNavigate?: () => Promise<void>;
    }): Promise<void> {
        // Show persistent header while restoring a token session (cookie or URL login).
        if (this.getToken()) {
            global.chromeReady(true)
        }
        if (!options?.skipLoadingBanner) {
            global.messageData({message: `((loading profile|td)) ${SPINNER}`});
        }
        return this.reqLogin
            .request(undefined, true)
            .then((user: ILogin) => {
                if (!user.permission) {
                    this.isLoggingOut(true);
                    global.chromeReady(false);
                    global.messageData({
                        message: 'error: no access to eadm',
                        type: 'warning'
                    });
                    const _cleanupFn = () => {
                        this.user(null);
                        this.removeToken();
                        setRouteHash('formlogin')
                        this.isLoggingOut(false);
                    };
                    return this.reqLogOut
                        .request()
                        .then(_cleanupFn)
                        .catch(_cleanupFn)
                        .then(() => false);
                }
                this.onLoginSuccess(user);
                return true;
            })
            .then((continueLogin: boolean) => {
                if (continueLogin !== true) {
                    return Promise.resolve();
                }
                return this.finishLoginBootstrap(options?.beforeNavigate);
            })
            .catch((reason: unknown) => {
                this.isFinishingLoginBootstrap(false);
                if (this.isNotAuthenticatedFailure(reason)) {
                    // not_authenticated handler already cleared session and opened dialog.
                    return;
                }
                global.messageData(createErrorMessage(String(reason ?? 'error'), `error: could not log in`));
                return Promise.reject(reason);
            });
    }

    /**
     * Load import formats before routing so AuthDataSource / master-data-source UI can resolve names on first paint.
     * eHub init stays after route handlers; Manager waits on {@link isFinishingLoginBootstrap}.
     */
    private finishLoginBootstrap(beforeNavigate?: () => Promise<void>): Promise<void> {
        this.isFinishingLoginBootstrap(true);
        return importFormatManager.init()
            .catch((reason: unknown) => {
                global.snackbarData(createErrorMessage(String(reason), `((startup incomplete|td))`));
            })
            .then(() => this.onLoginSuccessAfter(beforeNavigate))
            .then(() => {
                this.onLoginSuccessFinal();
                pubsub.trigger('login.success');
                const iseHubSupported = !!this.organizations().find(o => o.ehub);
                return iseHubSupported ? global.eHubManager.init() : undefined;
            })
            .catch((reason: unknown) => {
                global.snackbarData(createErrorMessage(String(reason), `((startup incomplete|td))`));
            })
            .finally(() => {
                this.isFinishingLoginBootstrap(false);
            });
    }

    /** Matches PromiseRequest reject reason after returncode 1 or missing mandatory token. */
    private isNotAuthenticatedFailure(reason: unknown): boolean {
        return reason === lm.t('not authenticated');
    }

    doLogout(): Promise<void> {
        // Mid-session `#/logout` and boot may both fire; hide GUI via flag (not a blank route).
        if (this.isLoggingOut()) {
            return Promise.resolve();
        }
        this.stopCheckForMessages();
        this.isLoggingOut(true);
        global.chromeReady(false);

        if (Cookie.get(TOKEN_KEY_SUPER)) {
            this.user(null);
            this.removeToken();
            this.doSessionDurationEnd()
            // reload page (login manager will then switch back the super token as the regular token)
            window.location.reload();
            return Promise.resolve();
        }

        const user = this.user();
        if (user?.authenticationtype === AuthenticationTypeFlag.CUSTOM_SAML && user.identityproviderid) {
            const hub = config.getUrlIdentityHub().replace(/\/$/, '');
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = `${hub}/saml/slo/${encodeURIComponent(user.companyid)}/${user.identityproviderid}`;
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'token';
            input.value = this.getToken();
            form.appendChild(input);
            this.user(null);
            global.cacheManager.removeCache();
            this.removeToken();
            this.doSessionDurationEnd();
            document.body.appendChild(form);
            form.submit();
            return Promise.resolve();
        }

        global.messageData({message: `((logging out|td)) ${SPINNER}`});
        const _cleanupFn = () => {
            global.messageData(null);
            this.showLogoutMessage()
            this.user(null);
            global.cacheManager.removeCache();
            this.removeToken();
            this.doSessionDurationEnd()
            // Replace hash while still logging out so layout does not flash the previous route.
            setRouteHash('formlogin')
            this.isLoggingOut(false);
        };
        return this.reqLogOut
            .request()
            .then(_cleanupFn)
            // just fail silently and log out the user from the client
            .catch(_cleanupFn)
            ;
    }

    relogin(callBack?: () => void) {
        // Hide shell via flag — do not park on `#/login` (token URLs remain for boot SSO).
        this.isLoggingOut(true);
        global.chromeReady(false);
        global.messageData({message: `((updating profile|td)) ${SPINNER}`});
        this.reqLogin.request(undefined, true)
            .then((user: ILogin) => {
                this.onLoginSuccess(user);
                global.messageData(null);
                callBack?.();
            })
            .catch((reason: string) => {
                global.messageData(null);
                global.snackbarData(createErrorMessage(reason, `error: could not update profile`));
            })
            .finally(() => {
                this.isLoggingOut(false);
                global.chromeReady(!!this.user());
            });
    }

    /**
     * Set the working organization by id (must exist in {@link organizations}).
     * - announce (default true): snackbar when the selection actually changes; pass false to opt out.
     * - rewriteUrl (default true): run navigateForOrgChange; pass false when the hash already dictates the org.
     */
    setSelectedOrganizationById(
        id: string,
        options?: {announce?: boolean | undefined; rewriteUrl?: boolean | undefined},
    ) {
        const announce = options?.announce !== false;
        const rewriteUrl = options?.rewriteUrl !== false;
        if (!rewriteUrl) {
            this.silentOrgChange = true;
        }
        const org = this.organizations().find((o: IOrganization) => o.organizationid == id);
        if (org) {
            const _old = this.selectedOrganizationId();
            const _new = org.organizationid;
            if (_old != _new) {
                this.selectedOrganization(org);
                if (announce) {
                    const selectedOrg = this.selectedOrganization();
                    if (selectedOrg) {
                        global.snackbarData({
                            message: `switched to|target:${selectedOrg.organizationame}`,
                            duration: SNACKBAR_DURATION_MEDIUM,
                        });
                    }
                }
            }
        }
        window.requestAnimationFrame(() => {
            this.silentOrgChange = false;
        });
    }

    /** Apply route `{orgnr}` to the working org without rewriting the URL (hash already owns org). */
    syncSelectedOrganizationFromRoute(
        orgnr: string | null | undefined,
        options?: {announce?: boolean},
    ): void {
        if (!orgnr || !this.isLoggedIn()) {
            return;
        }
        this.setSelectedOrganizationById(orgnr, {
            rewriteUrl: false,
            announce: options?.announce,
        });
    }

    getOrganizationById(id: string): IOrganization | undefined {
        return this.organizations().find(o => o.organizationid == id);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Cookie token methods
    //------------------------------------------------------------------------------------------------------------------

    setToken(token: string) {
        if (token) {
            const isSecure = window.location.protocol.indexOf('https') == 0;
            const cookieAttributes: CookieAttributes = {
                expires: new Date(new Date().getTime() + SESSION_DURATION),
                sameSite: isSecure ? 'none' : 'strict',
                secure: isSecure,
            }

            Cookie.set(this.tokenKey, token, _.clone(cookieAttributes));

            if (config.DEBUG) {
                Cookie.set(this.tokenKey + "D", token, _.clone(cookieAttributes));
            }
        }
    }

    getToken(): string {
        return Cookie.get(this.tokenKey) ?? '';
    }

    removeToken() {
        Cookie.remove(this.tokenKey);
    }

    public getHomeOrg(): IOrganization | undefined {
        const user = this.user();
        if (!user) {
            return undefined;
        }
        return this.organizations().find((org: IOrganization) => org.organizationid === user.orgnr);
    }

    public setHomeOrg() {
        const homeOrg = this.getHomeOrg();
        if (homeOrg) {
            this.setSelectedOrganizationById(homeOrg.organizationid);
        }
    }

    /*******************************************************************************************************************
     * Private methods
     ******************************************************************************************************************/

    private onLoginSuccess(user: ILogin) {
        this.doSessionDurationStart()

        this.user(user);

        const organizations = _.sortBy(user.permission, (p: IOrganization) => p.organizationame.toLowerCase());
        this.organizations(organizations);

        // Prefer org from the boot/deep-link hash over shared localStorage (multi-window safe).
        // Fall back to last picker choice, then the user's home company.
        const hashOrgnr = getOrgnrFromHash(this.intendedHash ?? document.location.hash);
        const preferredOrgId: string =
            hashOrgnr || nsAmplify.store("storedOrganizationId") || user.companyid;

        const org = this.organizations().find((o: IOrganization) => o.organizationid == preferredOrgId)
            ?? this.organizations()[0];
        if (!org) {
            return;
        }

        this.selectedOrganization(org);
        nsClone.targetOrg(org)

        initStore();

        this.isLoggingIn(false);

        this.initSurvicateTracker()
        hubSpotSupport.init()
    }

    private onLoginSuccessAfter(beforeNavigate?: () => Promise<void>): Promise<void> {
        const user = this.user();
        if (!user) {
            return Promise.resolve();
        }
        const oldUserHash = nsAmplify.store(USER_HASH_KEY);
        const newUserHash = generateHash(JSON.stringify({
            id: user.id,
            ssn: getSyncFieldValue("Socialsecuritynumber", user.syncfields)
        }));
        this.isNewUser = oldUserHash != newUserHash;

        if (this.isNewUser) {
            // ...if a new user, let's remove everything from local storage
            global.cacheManager.removeCache();
            // ...and store the new hash
            nsAmplify.store(USER_HASH_KEY, newUserHash);
        }
        global.messageData(null);

        const navigate = (): void => {
            navigateAfterLoginSuccess({
                ...(this.redirectUrl ? {redirectUrl: this.redirectUrl} : {}),
                intendedHash: this.intendedHash,
                onIntendedHashConsumed: () => {
                    this.intendedHash = null;
                },
            });
        };

        if (beforeNavigate) {
            return beforeNavigate().then(navigate);
        }
        navigate();
        return Promise.resolve();
    }

    private onLoginSuccessFinal() {
        this.koSub.subscribeChanged(this.selectedOrganization, (_new, _old) => {
            if (_new && !this.silentOrgChange && _new?.organizationid != _old?.organizationid) {
                const currentRoute = global.router.currentRoute();
                if (currentRoute) {
                    navigateForOrgChange(_new.organizationid, currentRoute);
                }
            }
            pubsub.trigger('login.success.final');
        });
    }

    private isSuperUser(): boolean {
        try {
            return getEadmRole(this.selectedOrganization() ?? undefined) === Permission.SuperAdmin;
        } catch (err) {
            return false;
        }
    }

    private showLogoutMessage() {
        global.snackbarData({
            message: `${i('fa-sign-out')} ((message: you are now logged out))`,
            duration: SNACKBAR_DURATION_MEDIUM
        });
    }

    public numNewTasksAndMessages = ko.pureComputed((): number => {
        return this.numNewTasks() + this.numNewMessages();
    })

    public numNewTasks = ko.pureComputed((): number => {
        return (this.user()?.receivedmessages ?? []).filter(m => {
            return this.isEHubTaskOrMessage(m) && m.istask && m.completed === null;
        }).length;
    })

    public numNewMessages = ko.pureComputed((): number => {
        return (this.user()?.receivedmessages ?? []).filter(m => {
            return this.isEHubTaskOrMessage(m) && !m.istask && m.read === null;
        }).length;
    })

    private isEHubTaskOrMessage(obj: IRecievedMessage): boolean {
        return [
            6, // eHub User Task
            7, // eHub Manager Task
            8, // eHub User Message
            9, // eHub Manager Message
        ].includes(obj.messagetypeid)
    }

    private startCheckForMessages() {
        





        this.reqCheckForUpdates.abort();
        this.reqCheckForUpdates
            .request()
            .then((data: [boolean]) => {
                if (data?.includes(true)) {
                    global.cacheManager.removeCache('login');
                    global.messageData({message: `((loading updates|td)) ${SPINNER}`});
                    this.reqLogin.abort();
                    return this.reqLogin
                        .request(undefined, true)
                        .then((user: ILogin) => {
                            global.cacheManager.removeCache('searchobject');
                            global.cacheManager.removeCache('getobject');
                            global.cacheManager.removeCache('updatereceivedmessage');
                            const currentUser = this.user();
                            if (!currentUser) {
                                return;
                            }
                            const _old = currentUser.receivedmessages;
                            const _new = user.receivedmessages;
                            const _diff = _new.filter(n => !_old.find(o => n.id == o.id));
                            const _numTask = _diff.filter(o => o.istask).length
                            const _numMessage = _diff.filter(o => !o.istask).length
                            let message: string | undefined;
                            if (_numTask && _numMessage) {
                                message = '((you have received new form data and messages))'
                            } else if (_numTask) {
                                message = '((you have received a new form data))'
                            } else if (_numMessage) {
                                message = '((you have received a new message))'
                            }
                            if (!message) {
                                return;
                            }
                            message += ` <a href="${getHash('mymessages')}">((see details))</a>`;
                            currentUser.receivedmessages = user.receivedmessages;
                            this.user.valueHasMutated?.()
                            global.messageData(null);
                            global.snackbarData({message: message, duration: SNACKBAR_DURATION_LONG});
                        })
                        .catch((reason: string) => {
                            global.messageData(null);
                            global.snackbarData(createErrorMessage(reason, `loading updates failed`));
                        });
                }
            })
            .then(() => {
                this.checkForMessagesTimeoutId = setTimeout(() => this.startCheckForMessages(), CHECK_FOR_UPDATES_THROTTLE_DURATION);
            })
            .catch((reason: string) => {
                // do stuff here
            })
    }

    private stopCheckForMessages() {
        this.reqCheckForUpdates.abort();
        clearTimeout(this.checkForMessagesTimeoutId);
    }

    private doSessionDurationStart() {
        this.loginStart = new Date()
        this.loginEnd = null
        _d.groupCollapsed('Session started')
        _d.info('Start:', this.loginStart?.toLocaleTimeString() ?? 'unknown')
        _d.groupEnd()
    }

    private doSessionDurationEnd() {
        this.loginEnd = new Date();
        _d.groupCollapsed('Session ended')
        _d.info('Start:', this.loginStart?.toLocaleTimeString() ?? 'unknown')
        _d.info('End:', this.loginEnd?.toLocaleTimeString() ?? 'unknown')
        if (this.loginStart && this.loginEnd) {
            const mStart = moment(this.loginStart)
            const mEnd = moment(this.loginEnd)
            const diff = mEnd.diff(mStart, 'ms')
            _d.info(`Duration: ${moment.utc(diff).format('HH:mm:ss')}`)
        } else {
            _d.info(`Duration: unknown`)
        }
        _d.groupEnd()
    }

    private initSurvicateTracker() {
        if (config.DEBUG) {
            _d.log('Survicate not initialized; dev environment')
            return
        }

        if (global.loginManager.isEmulated()) {
            _d.log('Survicator tracker not executed; emulated user')
            return
        }

        const user = this.user();
        const homeOrg = this.getHomeOrg();
        if (!user || !homeOrg) {
            return;
        }

        const created = getRawSyncFieldValue('Created', user.syncfields);
        const momentCreated = convertFromServerString(created ?? '')
        const created_at = momentCreated.unix()
        const orgnr = homeOrg.organizationid
        const url = window.location.origin + getHash('userdetails', {id: user.id}, true, orgnr)

        try {
            window._sva?.destroyVisitor()
        } catch (err) {
        }

        const email = ((): string => {
            const syncfieldNames: TSyncfield_User[] = ['Upn', 'Email', 'Mail'];
            let email: string | undefined
            syncfieldNames.find(name => {
                const val = getRawSyncFieldValue(name, user.syncfields)
                if (val) {
                    return email = val
                }
            })
            return email || user.email
        })()

        const isProfessionalUser = [
            Permission.SuperEmployee,
            Permission.DepartmentAdmin,
            Permission.SuperManager,
            Permission.CompanyAdmin,
            Permission.SuperAdmin,
        ].includes(getEadmRole());

        const rolePersona: TUserRolePersona = ((): TUserRolePersona => {
            switch(getEadmRole()) {
                case Permission.SuperEmployee:
                case Permission.DepartmentAdmin:
                case Permission.SuperManager:
                case Permission.CompanyAdmin:
                    return 'External stakeholder'
                case Permission.OrganizationAdmin:
                    return 'Partner'
                case Permission.SuperAdmin:
                    return 'Internal employee'
                default:
                    return 'End Consumer'
            }
        })()

        const traits: any = {
            email:                                                     email,
            signed_up:                                                 created_at,
            company_name:                                              homeOrg.organizationame,
            company_id:                                                homeOrg.organizationid,
            role:                                                      getPermissionDefinition(getEadmRole() ?? Permission.None).serverName,
            url:                                                       url,
            user_id:                                                   user.id,

            mandatory_visma_reporting_user_is_professional_user:       isProfessionalUser,
            mandatory_visma_reporting_user_works_in_accounting_office: false,
            user_role_persona:                                         rolePersona,
            user_department:                                           getRawSyncFieldValue('Department', user.syncfields),
            user_role_functional:                                      getRawSyncFieldValue('AWorkClassificationName', user.syncfields),
            user_OS:                                                   window.navigator.userAgent,
            user_device:                                               window.navigator.userAgent,
            usage_frequency:                                           null,
            user_platform:                                             'Web',
            user_country:                                              null,
            customer_size:                                             null,
        };

        _d.groupCollapsed('Survicate tracker options')
        _d.log(traits)
        _d.groupEnd()

        _d.log('Survicate initialization start')
        const script: HTMLScriptElement = document.createElement('script')
        script.setAttribute('type', 'text/javascript')
        script.setAttribute('async', 'true')
        script.setAttribute('defer', 'true')
        script.src = 'https://survey.survicate.com/workspaces/07eafd28f173ba5cba06817c47593733/web_surveys.js'
        script.addEventListener('load', () => {
            let count = 0;
            const fn = () => {
                new Promise((resolve, reject) => {
                    count++
                    try {
                        if (count > 100) {
                            _d.warn('Survicate tracker failed to initialize')
                        } else {
                            const _sva = window._sva as import("@survicate/survicate-web-package").SurveyApi;
                            _sva.setVisitorTraits(traits)
                            _d.log(`Survicate initialized (tries: ${count})`)
                        }
                    } catch(err) {
                        setTimeout(fn, 1000)
                    }
                })
            }
            fn()
        })
        document.body.append(script)
    }

    /*******************************************************************************************************************
     * Miscellaneous
     ******************************************************************************************************************/

    dispose() {
        this.koSub.unsuscribeAll();
        this.reqLogin.abort();
        this.stopCheckForMessages();
    }
}
